<template>
	<div class="patchConfirmRoot" role="dialog" aria-labelledby="patchConfirmTitle" aria-describedby="patchConfirmBody">
		<div class="titleBar">
			<div id="patchConfirmTitle" class="title" role="alert">Install Windows updates on {{serverName}}</div>
			<div class="subtitle">Read every point below.  Sliding the control at the bottom starts the installation immediately.</div>
		</div>
		<div id="patchConfirmBody" class="dialogBody" ref="dialogBody">
			<div v-if="dryRun" class="dryRunTag">
				SIMULATED (dry-run mode) — this instance is not the authorized orchestrator.  No server is contacted and nothing is installed.
			</div>

			<div class="impactBox" role="alert">
				<div class="impactTitle">What this does to {{serverName}}</div>
				DbServerAgent searches Windows Update, installs what the rules below select <strong>one update at a time</strong>, and when an
				update needs it, <strong>stops SQL Server and reboots the server</strong> — as many times as the passes require.  The server is
				unavailable for minutes at a time and the whole job can take hours.  It runs to completion on its own even if TdsAppService
				restarts; there is no way to abort an installation that has started.
			</div>

			<div class="noteBox">
				<strong>The web apps are not using {{serverName}}</strong> right now (they are on <code class="inline">{{activeServerName}}</code>), which is
				why this is allowed.  While the job runs, <strong>no migration or cutover involving {{serverName}} can start</strong> — including the
				automated maintenance window.  Restore traffic to {{serverName}} only after the job has finished.
			</div>

			<div class="kvGrid">
				<span class="kvKey">Server</span>
				<span><code class="inline">{{serverName}}</code><template v-if="host && host !== serverName"> ({{host}})</template><template v-if="role"> — {{role}}</template></span>
				<template v-if="agent">
					<span class="kvKey">Agent</span>
					<span>
						<template v-if="agent.agentVersion">DbServerAgent {{agent.agentVersion}}</template><template v-else>DbServerAgent</template><template v-if="agent.osVersion"> on {{agent.osVersion}}</template>
						<template v-if="agent.lastBootUtc"> — last booted {{formatDateTime(agent.lastBootUtc)}}</template>
					</span>
					<span class="kvKey">SQL Server service</span>
					<span>
						<code class="inline">{{agent.sqlServiceName || 'MSSQLSERVER'}}</code> is {{(agent.sqlServiceStatus || 'unknown').toLowerCase()}} — it is stopped gracefully before each reboot and started again by Windows.
					</span>
					<template v-if="agent.rebootPending">
						<span class="kvKey">Reboot pending</span>
						<span class="warnText">Windows already has a reboot pending on this server; the job will perform it.</span>
					</template>
				</template>
				<span class="kvKey">Which updates</span>
				<span>
					<template v-if="canaryFilter">
						Only updates <code class="inline">{{canarySource}}</code> already has (the canary rule), so nothing reaches {{serverName}} that has not
						been running on {{canarySource}} for a while.<template v-if="policy && policy.canaryExemptSqlUpdates">
						SQL Server updates are the exception: <code class="inline">{{canarySource}}</code> cannot vouch for them (the same fix ships on two
						servicing branches under different KB numbers), so they wait {{policy.sqlUpdateMinimumAgeDays}} day{{policy.sqlUpdateMinimumAgeDays === 1 ? '' : 's'}}
						after Microsoft published them instead.</template>
					</template>
					<template v-else-if="isCanary">
						<strong>Every applicable update.</strong>  {{serverName}} is the canary: it takes updates first, and the primary later gets only what
						this server already has.
					</template>
					<template v-else>
						<strong>Every applicable update</strong> — the canary filter is switched off in settings.
					</template>
				</span>
				<template v-if="policy">
					<span class="kvKey">Source</span>
					<span>{{updateServiceText}}<template v-if="!policy.includeDefinitionUpdates"> — Defender definition updates are skipped (they ship daily and Defender updates itself)</template>.</span>
					<span class="kvKey">Passes</span>
					<span>up to {{policy.maxPasses}} scan/install/reboot pass{{policy.maxPasses === 1 ? '' : 'es'}}, stopping when nothing applicable is left</span>
					<span class="kvKey">Limits</span>
					<span>
						tracked for at most {{formatMinutes(policy.timeoutMinutes)}}<template v-if="policy.rebootWaitMinutes">, and a reboot may take {{formatMinutes(policy.rebootWaitMinutes)}} before the job is called failed</template>.
						<span class="mutedText">After the tracking deadline DbServerAgent still finishes the job; the lockout stays until it reports it done.</span>
					</span>
					<template v-if="policy.minimumAgeDays > 0">
						<span class="kvKey">Minimum age</span>
						<span>nothing published in the last {{policy.minimumAgeDays}} day{{policy.minimumAgeDays === 1 ? '' : 's'}}</span>
					</template>
					<template v-if="policy.excludedKbs && policy.excludedKbs.length">
						<span class="kvKey">Never installed</span>
						<span>{{policy.excludedKbs.join(', ')}}</span>
					</template>
				</template>
			</div>

			<div class="sectionTitle">What will happen, in order</div>
			<ol class="stepList">
				<li>DbServerAgent on {{serverName}} is asked to start a patch job (idempotent: a TdsAppService restart re-attaches to it instead of starting a second one).</li>
				<li v-if="canaryFilter">The list of updates <code class="inline">{{canarySource}}</code> has installed in the last {{policy ? policy.canaryHistoryDays : 120}} days is read and sent as the approved set.</li>
				<li>Each pass: search Windows Update, apply the selection rules, download the selected updates, then install them one at a time (servicing-stack updates first).</li>
				<li>If an update requires a reboot: SQL Server is stopped gracefully, the server reboots, and the agent resumes the job when it comes back.</li>
				<li>A final read-only scan reports how many applicable updates remain for a later job.</li>
				<li>You get an email with the outcome and the per-update results, and this page shows them.</li>
			</ol>

			<div v-if="warnings.length" class="warningBox">
				<strong>{{warnings.length}} warning{{warnings.length === 1 ? '' : 's'}} — not blocking, but read before continuing:</strong>
				<ul>
					<li v-for="(w, i) in warnings" :key="i">{{w}}</li>
				</ul>
			</div>
		</div>
		<div class="footer">
			<div class="slideWrap slideRed">
				<slide-button :key="'slide_patchConfirm_' + serverName"
							  :auto-width="false"
							  :circle="true"
							  :noanimate="true"
							  :width="340"
							  :height="50"
							  :text="'slide to patch ' + serverName"
							  success-text="starting"
							  :requireReleaseToComplete="true"
							  @completed="onConfirm" />
			</div>
			<div ref="closeBtn" class="dialogButton cancelButton" role="button" tabindex="0" @click="DefaultClose" @keydown.space.enter.prevent="DefaultClose">Cancel</div>
		</div>
	</div>
</template>

<script>
	// Confirmation dialog for a manual Windows Update installation on one database server.  Opened through
	// ModalDialog(PatchConfirmPopup, props, options); resolves true when the administrator completes the slide control.
	// Everything it states comes from the server's own patching status (DbMaintenanceServerPatchStatus + DbMaintenancePatchPolicy),
	// so the dialog can never promise a policy the service would not apply.
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import { formatDateTime } from './runFormat';

	export default {
		components: { SlideButton },
		props:
		{
			/** DbMaintenanceServerPatchStatus of the server to patch. */
			server: {
				type: Object,
				required: true
			},
			/** DbMaintenancePatchPolicy in force, or null. */
			policy: {
				type: Object,
				default: null
			},
			/** The database server the web apps are on (shown as the reason this is allowed). */
			activeServer: {
				type: String,
				default: null
			},
			/** The canary server (the other configured endpoint), whose installed updates gate what the primary may install. */
			canaryName: {
				type: String,
				default: null
			},
			/** True when the feature runs in dry-run (simulated) mode. */
			dryRun: {
				type: Boolean,
				default: false
			}
		},
		computed:
		{
			serverName()
			{
				return this.server.name || '?';
			},
			host()
			{
				return this.server.host;
			},
			role()
			{
				return this.server.role;
			},
			agent()
			{
				return this.server.agent;
			},
			activeServerName()
			{
				return this.activeServer || 'the other server';
			},
			isCanary()
			{
				return this.server.role === 'fallback';
			},
			/** The canary filter applies to the primary only, and only when it is switched on in settings. */
			canaryFilter()
			{
				return !this.isCanary && !!(this.policy && this.policy.canaryFilter);
			},
			canarySource()
			{
				return this.canaryName || 'the fallback';
			},
			updateServiceText()
			{
				switch (this.policy ? this.policy.updateService : null)
				{
					case 'MicrosoftUpdate': return 'Microsoft Update (Windows plus SQL Server and Office updates)';
					case 'Default': return 'whatever Windows Update policy on the server configures (a WSUS server when one is set)';
					default: return 'Windows Update, named explicitly so a policy change cannot redirect the search';
				}
			},
			warnings()
			{
				let list = [];
				let a = this.agent;
				if (a)
				{
					if (a.stub)
						list.push('The readiness facts shown are synthetic (dry-run stub mode); no server was contacted.');
					if (a.stale)
						list.push('The readiness facts are a few seconds old (a fresh probe is running).');
					if (a.pendingFileRenames && !a.rebootPending)
						list.push('The server has pending file-rename operations.  Installers set these routinely; it is not by itself a pending reboot.');
					if (a.clockSkewSeconds >= 10)
						list.push('The agent’s clock differs from this service’s by about ' + a.clockSkewSeconds + ' s.  The signed request tokens allow 60 s; fix time synchronisation before this grows.');
				}
				if (this.server.manualJob && !this.server.manualJob.active && this.server.manualJob.status !== 'Succeeded')
					list.push('The last manual job on this server ended with "' + this.server.manualJob.status + '"' + (this.server.manualJob.summary ? ': ' + this.server.manualJob.summary : '.'));
				if (this.policy && !this.policy.canaryFilter && !this.isCanary)
					list.push('The canary filter is off, so this server receives updates the fallback has never run.');
				return list;
			}
		},
		methods:
		{
			formatDateTime,
			formatMinutes(minutes)
			{
				let m = Number(minutes);
				if (!m || isNaN(m))
					return 'an unconfigured time';
				if (m < 60)
					return m + ' min';
				let h = m / 60;
				return (h % 1 === 0 ? h : h.toFixed(1)) + ' h';
			},
			SetFocus()
			{
				if (this.$refs.closeBtn)
					this.$refs.closeBtn.focus();
			},
			DefaultClose()
			{
				this.$emit('close', false);
			},
			onConfirm()
			{
				this.$emit('close', true);
			}
		}
	}
</script>

<style scoped>
	.patchConfirmRoot
	{
		background-color: #FFFFFF;
		min-width: 320px;
		max-width: min(900px, calc(100vw - 30px));
		max-height: calc(100vh - 30px);
		display: flex;
		flex-direction: column;
		word-break: break-word;
	}

	.titleBar
	{
		padding: 12px 16px 8px 16px;
		border-bottom: 1px solid #E0E0E0;
		flex: 0 0 auto;
	}

	.title
	{
		text-align: center;
		font-weight: bold;
		font-size: 16pt;
	}

	.subtitle
	{
		text-align: center;
		color: #555555;
		font-size: 0.9em;
		margin-top: 2px;
	}

	.dialogBody
	{
		padding: 10px 16px 12px 16px;
		overflow-y: auto;
		flex: 1 1 auto;
		min-height: 0;
	}

	.footer
	{
		flex: 0 0 auto;
		border-top: 1px solid #E0E0E0;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: space-between;
		gap: 10px 16px;
		padding: 10px 16px;
	}

	.dialogButton
	{
		display: inline-block;
		cursor: pointer;
		font-weight: bold;
		font-size: 12pt;
		padding: 10px 22px;
		border-radius: 5px;
		color: #CE7D29;
		border: 1px solid #DDDDDD;
		margin-left: auto;
	}

		.dialogButton:hover,
		.dialogButton:focus
		{
			background-color: rgba(0,0,0,0.05);
		}

	.slideWrap :deep(.slideunlock)
	{
		margin: 0;
	}

	.slideRed :deep(.slideunlock)
	{
		--su-color-bg: #F5DCDC;
		--su-color-progress-normal-bg: #E5AFAF;
		--su-color-progress-complete-bg: #CC3333;
	}

	.dryRunTag
	{
		background: repeating-linear-gradient(135deg, #FFB347 0px, #FFB347 14px, #FFC97A 14px, #FFC97A 28px);
		border: 2px solid #A85A00;
		color: #3A2000;
		font-weight: bold;
		padding: 8px 12px;
		margin-bottom: 10px;
	}

	.impactBox
	{
		background-color: #FFE0E0;
		border: 2px solid #C00000;
		color: #660000;
		padding: 10px 14px;
		margin-bottom: 10px;
		line-height: 1.5;
	}

	.impactTitle
	{
		font-weight: bold;
		font-size: 1.15em;
		margin-bottom: 4px;
	}

	.noteBox
	{
		background-color: #F4F7FB;
		border: 1px solid #C9D8EA;
		color: #1F3A5C;
		padding: 8px 12px;
		margin-bottom: 10px;
		line-height: 1.5;
	}

	.warningBox
	{
		background-color: #FFEFC9;
		border: 1px solid #E0B93F;
		color: #7A5200;
		padding: 8px 12px;
		margin: 10px 0 0 0;
	}

		.warningBox ul
		{
			margin: 4px 0 0 0;
			padding-left: 22px;
		}

	.warnText
	{
		color: #7A5200;
		font-weight: bold;
	}

	.mutedText
	{
		color: #888888;
	}

	.kvGrid
	{
		display: grid;
		grid-template-columns: max-content 1fr;
		gap: 4px 14px;
		align-items: baseline;
		margin: 10px 0;
	}

	.kvKey
	{
		font-weight: bold;
		color: #444444;
		white-space: nowrap;
	}

	.sectionTitle
	{
		font-weight: bold;
		font-size: 1.05em;
		border-bottom: 1px solid #E0E0E0;
		padding-bottom: 3px;
		margin: 14px 0 6px 0;
	}

	.stepList
	{
		margin: 0;
		padding-left: 22px;
		line-height: 1.6;
	}

	code.inline
	{
		font-family: Consolas, monospace;
		background-color: #F4F4F4;
		border-radius: 3px;
		padding: 0px 5px;
	}
</style>
